<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import Spinner from '@/components/ui/Spinner.vue'
import DemoVideoModal from '@/components/modals/DemoVideoModal.vue'
import AddTvPlayoutModal from '@/components/modals/AddTvPlayoutModal.vue'
import PlayoutItemV2 from '@/components/TVPlayout/PlayoutItemV2.vue'
import PlayoutListItemV2 from '@/components/TVPlayout/PlayoutListItemV2.vue'
import PlayoutEmptyPlaceholderV2 from '@/components/TVPlayout/PlayoutEmptyPlaceholderV2.vue'
import ViewModeSwitcher from '@/components/ui/ViewModeSwitcher.vue'
import ResponsiveToolbar from '@/components/ui/ResponsiveToolbar.vue'
import ListingSearchInput from '@/components/ui/ListingSearchInput.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import IconCheckmark from '@/components/icon/IconCheckmark.vue'
import IconChevronUpDown from '@/components/icon/IconChevronUpDown.vue'
import { sortStreams } from '@/utils'
import { usePersistedSort, DATE_NAME_SORT } from '@/Utils/usePersistedSort'
import IconLock from '@/components/icon/IconLock.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import PageHeader from '@/components/ui/PageHeader.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ListingCreateTile from '@/components/ui/ListingCreateTile.vue'

const store = useStore()
const router = useRouter()

const showPlayoutModal = ref()
const modalDEMOVideo = ref(null)
const sortDropdown = ref(null)
const searchWidgetInput = ref('')
const { sortBy, setSort } = usePersistedSort('castr.sort.playout', DATE_NAME_SORT, 'Newest')
const sortByLabel = computed(() => sortBy.value)

const playouts = computed(() => store.getters['Streams/tvPlayouts'])
const loadingStatus = computed(() => store.getters['Ui/loadingStatus'])
const shouldShowCompactModeStreams = computed(() => store.getters['Ui/shouldShowCompactModeStreams'])
const isLiveSubscriptionExpired = computed(() => store.getters['User/isLiveSubscriptionExpired'])
const hasLiveSubscription = computed(() => store.getters['User/hasLiveSubscription'])
const maxTvPlayoutItems = computed(() => store.getters['User/maxTvPlayoutItems'])

const tvPlayoutsFiltered = computed(() => {
	const q = searchWidgetInput.value.trim().toLowerCase()
	const list = !q ? (playouts.value || []) : (playouts.value || []).filter((p) => p.name?.toLowerCase().includes(q))
	return sortStreams(list, sortBy.value) || list
})

const usageLabel = computed(() => {
	const used = playouts.value?.length ?? 0
	const max = maxTvPlayoutItems.value
	if (max === undefined || max === null || max === '') return null
	return `${used}/${max}`
})

const hasPlayouts = computed(() => (playouts.value?.length ?? 0) > 0)
const showListControls = computed(() => (playouts.value?.length ?? 0) > 2)

// At quota: disable create affordances and surface an Upgrade hint.
const atLimit = computed(() => {
	const max = Number(maxTvPlayoutItems.value)
	if (!max) return false
	return (playouts.value?.length ?? 0) >= max
})

const playoutLimit = computed(() => {
	const c = Number(maxTvPlayoutItems.value)
	return Number.isFinite(c) && c > 0 ? c : null
})

// `used` is this workspace's; the meter pairs it with the account's total, so
// an empty workspace still shows a plan that is being spent elsewhere.
const playoutHeaderUsage = computed(() => {
	if (loadingStatus.value) return null
	const meta = {
		used: playouts.value?.length || 0,
		label: 'playouts',
		labelSingular: 'playout',
		resource: 'tvplayout',
	}
	if (playoutLimit.value) meta.limit = playoutLimit.value
	return meta
})

onMounted(async () => {
	store.dispatch('Ui/setLoadingStatus', true)
	await store.dispatch('Streams/getStreams')
	store.dispatch('Ui/setLoadingStatus', false)
})

const showCreatePlayoutModal = () => {
	showPlayoutModal.value?.toggleSidebar()
}

const showDemoModal = () => {
	modalDEMOVideo.value?.show()
}

const changeSortBy = (val) => {
	setSort(val)
	sortDropdown.value?.toggleDropdown()
}

const onNewTv = (player) => {
	if (player?._id) {
		store.dispatch('Streams/addStream', player)
	}
	router.push({
		name: 'PlayoutViewV2',
		params: { tvId: player._id },
	})
	store.dispatch('Streams/getStreams', true)
}
</script>

<template>
	<div class="container detail-shell px-4">
		<PageHeader
			title="TV Playout"
			subtitle="Program a 24/7 channel from scheduled live and recorded content."
			mobile-inline
			:usage="!loadingStatus ? playoutHeaderUsage : null"
		>
			<template v-if="!loadingStatus && hasLiveSubscription && !isLiveSubscriptionExpired">
				<Tooltip v-if="atLimit" wrap-trigger :content="`You've reached your TV Playout limit (${usageLabel}). Upgrade for more.`">
					<FormButton class="gap-1" size="md" disabled>
						<icon-base class="mr-2">
							<icon-plus />
						</icon-base>
						New <span class="hidden md:block">playout</span>
					</FormButton>
				</Tooltip>
				<FormButton v-else class="gap-1" size="md" @click="showCreatePlayoutModal">
					<icon-base class="mr-2">
						<icon-plus />
					</icon-base>
					New <span class="hidden md:block">playout</span>
				</FormButton>
			</template>
		</PageHeader>
		<Spinner
			v-if="loadingStatus"
			text="Loading your playouts…"
			classes="text-surface-8 mt-96"
			spinner-color="var(--c-dark-8)"
			spinner-size="15px"
		/>
		<main v-else-if="!hasLiveSubscription || isLiveSubscriptionExpired">
			<EmptyState title="Upgrade to unlock TV Playout"
				subtitle="Schedule pre-recorded videos and live sources into a continuous, always-on channel that runs around the clock, like a professional TV station. It's not included in your current plan.">
				<template #icon>
					<icon-base width="28" height="28" fill="none">
						<icon-lock />
					</icon-base>
				</template>
				<template #actions>
					<FormButton to="/subscribe">Upgrade plan</FormButton>
				</template>
			</EmptyState>
		</main>
		<main v-else>
			<PlayoutEmptyPlaceholderV2
				v-if="!hasPlayouts"
				@create-playout="showCreatePlayoutModal"
				@show-demo="showDemoModal"
			/>
			<section v-else class="mt-5">
				<div class="toolbar-container tab-settings">
					<ResponsiveToolbar class="my-3">
						<template v-if="showListControls">
						<DropDownMenu ref="sortDropdown" type="secondary" position="left">
							<template #toggle-button>
								<span class="text-surface-8">Sort by:&nbsp;</span>
								<span class="text-white">{{ sortByLabel }}</span>
								<icon-base class="!ml-1 shrink-0 text-surface-8">
									<icon-chevron-up-down />
								</icon-base>
							</template>
							<template #menu-items>
								<DropDownMenuItem :active="sortBy === 'Oldest'" @click="changeSortBy('Oldest')">
									Oldest
									<icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Oldest'" fill="none">
										<icon-checkmark />
									</icon-base>
								</DropDownMenuItem>
								<DropDownMenuItem :active="sortBy === 'Newest'" @click="changeSortBy('Newest')">
									Newest
									<icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Newest'" fill="none">
										<icon-checkmark />
									</icon-base>
								</DropDownMenuItem>
								<DropDownMenuItem :active="sortBy === 'A to Z'" @click="changeSortBy('A to Z')">
									A to Z
									<icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'A to Z'" fill="none">
										<icon-checkmark />
									</icon-base>
								</DropDownMenuItem>
								<DropDownMenuItem :active="sortBy === 'Z to A'" @click="changeSortBy('Z to A')">
									Z to A
									<icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Z to A'" fill="none">
										<icon-checkmark />
									</icon-base>
								</DropDownMenuItem>
							</template>
						</DropDownMenu>
						<ListingSearchInput v-model="searchWidgetInput" />
						</template>
						<ViewModeSwitcher class="shrink-0" />
					</ResponsiveToolbar>
				</div>

				<p
					v-if="searchWidgetInput && !tvPlayoutsFiltered.length"
					class="text-sm text-surface-7 text-center py-10 m-0"
				>
					No playouts match “{{ searchWidgetInput }}”
				</p>

				<!-- Grid view -->
				<ul v-else-if="!shouldShowCompactModeStreams" class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 auto-rows-fr gap-4">
					<li v-for="playout in tvPlayoutsFiltered" :key="playout._id">
						<PlayoutItemV2 :playout="playout" />
					</li>
					<li v-if="!atLimit && !searchWidgetInput">
						<ListingCreateTile label="playout" @create="showCreatePlayoutModal" />
					</li>
					<li v-else-if="atLimit && !searchWidgetInput">
						<ListingCreateTile label="playout" :used="playouts.length" :limit="maxTvPlayoutItems" limit-reached />
					</li>
				</ul>
				<!-- List view -->
				<ul v-else class="grid grid-cols-1 auto-rows-fr gap-1.5">
					<li v-for="playout in tvPlayoutsFiltered" :key="playout._id">
						<PlayoutListItemV2 :playout="playout" />
					</li>
					<li v-if="!atLimit && !searchWidgetInput">
						<ListingCreateTile label="playout" view="list" @create="showCreatePlayoutModal" />
					</li>
					<li v-else-if="atLimit && !searchWidgetInput">
						<ListingCreateTile label="playout" view="list" :used="playouts.length" :limit="maxTvPlayoutItems" limit-reached />
					</li>
				</ul>
			</section>
		</main>
		<demo-video-modal ref="modalDEMOVideo" videoid="cmv94582p0q0iqmy8phrv1txr" />
		<add-tv-playout-modal
			ref="showPlayoutModal"
			version="v2"
			@playout-added="onNewTv"
		/>
	</div>
</template>

<style scoped></style>
